// Phase-A R1.2 — Breadcrumb row. WORKSPACE-only, 36px tall.
// At <md (<768) and >3 crumbs: truncate to first › … › last-2 with a
// Radix Popover on the … that shows the hidden hops as clickable links.
// S-21 — the "future resolver hook" promised in breadcrumb-map.ts landed:
// for entity routes the raw URL param (a cuid) resolves to the entity's
// TITLE (course title, exam title, session title) via a module-level cache
// (one fetch per entity per SPA lifetime; failure caches the raw fallback).
// S-I18N-W6-SWEEP — copy moved into the `shell` catalogue (`breadcrumb.*` for
// the route ids, `breadcrumbUi.*` for this file's own strings). The component
// reads it through `t`; the PARAM_RESOLVERS below are plain async module
// functions with no React context, so they use the documented `translate`
// seam instead (src/i18n/locale-state.ts).
import React from "react";
import * as Popover from "@radix-ui/react-popover";
import { useTranslation } from "react-i18next";
import { assessmentsApi, catalogApi, classSessionsApi, marketApi } from "../api/endpoints-core.js";
import { useAuth } from "../auth/AuthContext";
import { useCurrentRoute, useGo } from "../router";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { translate } from "../i18n/locale-state";
import { BidiText } from "../i18n/BidiText";
import { useStableT } from "../i18n/stable-t";
import { breadcrumbLabel } from "../router/breadcrumb-map";

interface Crumb { label: string; routeId?: string; isLast: boolean }
interface Ellipsis { ellipsis: true; hidden: Crumb[] }

// route-id → async param→title resolver. Only entity routes are listed;
// everything else keeps the static breadcrumbLabel(param) fallback.
// S-MKT-PRO-W1 — labels for the routed studio sub-segments (…/market/studio/:id/<tab>).
const MARKET_STUDIO_SEGMENTS = [
  "overview", "curriculum", "content", "pricing", "coupons", "analytics", "settings", "preview", "record",
] as const;

const marketStudioSegmentLabel = (seg: string): string | undefined =>
  (MARKET_STUDIO_SEGMENTS as readonly string[]).includes(seg)
    ? translate("shell:breadcrumbUi.studioTab." + seg, seg)
    : undefined;

const PARAM_RESOLVERS: Record<string, (param: string) => Promise<string | null>> = {
  course: async (p) => ((await catalogApi.getCourse(p)) as { title?: string }).title ?? null,
  // S-MKT-PRO-W1 — /market/studio/:id[/tab] crumbs read «<course title> · <tab>»
  // instead of a raw cuid path. Author-gated endpoint: a denied/foreign id caches
  // the raw fallback (same no-leak semantics as the other resolvers).
  market: async (p) => {
    const [seg, id, sub] = p.split("/");
    if (seg !== "studio" || !id) return null;
    if (id === "new") return translate("shell:breadcrumbUi.newCourse", "ساخت دورهٔ جدید");
    if (id === "bundles") return null;
    const c = (await marketApi.course(id)) as { title?: string };
    if (!c.title) return null;
    const tabLabel = sub ? marketStudioSegmentLabel(sub) : undefined;
    return tabLabel ? `${c.title} · ${tabLabel}` : c.title;
  },
  // S-CRED W2 — /credential/print/:id. بدونِ این، آخرین تکهٔ مسیر خامِ
  // «print/<cuid>» روی نان‌ریزهٔ یک صفحهٔ فارسی چاپ می‌شد. هیچ فراخوانِ APIای
  // لازم نیست: عنوانِ سند ثابت است و شناسه اصلاً نباید در نان‌ریزه دیده شود.
  // W3: همان مسیر تخصص‌کارت را هم چاپ می‌کند، پس برچسب عامِ «مدرک» شد — نوعِ
  // سند فقط با یک فراخوانِ API معلوم می‌شد و نان‌ریزه ارزشش را ندارد.
  credential: async (p) =>
    p.startsWith("print/") ? translate("shell:breadcrumbUi.printCredential", "چاپِ مدرک") : null,
  "course-live": async (p) => ((await catalogApi.getCourse(p)) as { title?: string }).title ?? null,
  gradebook: async (p) => ((await catalogApi.getCourse(p)) as { title?: string }).title ?? null,
  exam: async (p) => ((await assessmentsApi.get(p)) as { title?: string }).title ?? null,
  "assessment-live": async (p) => ((await assessmentsApi.get(p)) as { title?: string }).title ?? null,
  "live-class": async (p) => {
    // param may carry a sub-path (<id>/report | <id>/lobby).
    const [id, sub] = p.split("/");
    const s = (await classSessionsApi.get(id)) as { title?: string };
    if (!s.title) return null;
    if (sub === "report") return s.title + " · " + translate("shell:breadcrumbUi.sessionReport", "گزارش");
    if (sub === "lobby") return s.title + " · " + translate("shell:breadcrumbUi.sessionLobby", "لابی");
    return s.title;
  },
};

const paramLabelCache = new Map<string, string>();
// S-21 review fix — the cache must never outlive the auth IDENTITY: logout is
// a pure SPA transition (no reload), so a bare module-level Map would serve
// user A's resolved titles to user B in the same tab (cross-user/tenant title
// disclosure — server no-leak-404 never re-runs on a cache hit), and a denied
// user's negative "" entry would stick for a later authorized user. The cache
// is therefore owned by one tenant:user identity and cleared on any change.
let cacheOwner = "";
const ensureCacheOwner = (owner: string): void => {
  if (owner !== cacheOwner) {
    cacheOwner = owner;
    paramLabelCache.clear();
  }
};

/** Resolved entity title for the param crumb, or null → raw fallback. */
const useBreadcrumbParamLabel = (routeId: string, param: string | null, owner: string): string | null => {
  // Must run BEFORE the useState initializer reads the cache.
  ensureCacheOwner(owner);
  const key = routeId + ":" + (param ?? "");
  const [label, setLabel] = React.useState<string | null>(() =>
    param ? paramLabelCache.get(key) || null : null,
  );
  React.useEffect(() => {
    if (!param || !PARAM_RESOLVERS[routeId]) {
      setLabel(null);
      return;
    }
    const cached = paramLabelCache.get(key);
    if (cached !== undefined) {
      setLabel(cached || null);
      return;
    }
    let alive = true;
    PARAM_RESOLVERS[routeId](param)
      .then((t) => {
        paramLabelCache.set(key, t ?? "");
        if (alive) setLabel(t ?? null);
      })
      .catch(() => {
        // Cache the failure too — a 404/denied entity must not refetch on
        // every breadcrumb render.
        paramLabelCache.set(key, "");
        if (alive) setLabel(null);
      });
    return () => {
      alive = false;
    };
  }, [key, routeId, param]);
  return label;
};

const Sep: React.FC = () => <span aria-hidden="true" className="breadcrumb-sep">›</span>;

export const Breadcrumbs: React.FC = () => {
  const { id: route, param } = useCurrentRoute();
  // `shell` is a BOOT namespace — awaited before the first paint — so the row
  // never flashes raw keys, and `t` here re-renders the crumbs on a language
  // switch (the `translate` seam inside the resolvers cannot).
  const needsProfile = route === "settings" && param === "notifications";
  const { t: rawT, ready } = useTranslation(needsProfile ? ["shell", "profile"] : ["shell"]);
  const t = useStableT(rawT);
  const go = useGo();
  const isMd = useMediaQuery("(min-width: 768px)");
  const { user } = useAuth();
  const resolvedParamLabel = useBreadcrumbParamLabel(
    route,
    param,
    (user?.tenantId ?? "") + ":" + (user?.userId ?? ""),
  );

  const crumbs: Crumb[] = [];
  if (route !== "home" && route !== "") {
    crumbs.push({ label: breadcrumbLabel("home", t), routeId: "home", isLast: false });
  }
  if (route && route !== "home") {
    crumbs.push({ label: breadcrumbLabel(route, t), routeId: route, isLast: !param || (needsProfile && !ready) });
  }
  if (param && (!needsProfile || ready)) crumbs.push({ label: resolvedParamLabel ?? breadcrumbLabel(param, t), isLast: true });
  if (crumbs.length === 0) crumbs.push({ label: breadcrumbLabel("home", t), isLast: true });

  const shouldTruncate = !isMd && crumbs.length > 3;
  const items: Array<Crumb | Ellipsis> = shouldTruncate
    ? [crumbs[0], { ellipsis: true, hidden: crumbs.slice(1, -2) }, ...crumbs.slice(-2)]
    : crumbs;

  return (
    <nav aria-label={t("breadcrumbUi.nav")} className="breadcrumb-row">
      <ol>
        {items.map((c, i) => {
          const isTail = i === items.length - 1;
          if ("ellipsis" in c) {
            return (
              <li key={`e-${i}`} className="breadcrumb-ellipsis">
                <Popover.Root>
                  <Popover.Trigger asChild>
                    <button type="button" aria-label={t("breadcrumbUi.showFullPath")}>…</button>
                  </Popover.Trigger>
                  <Popover.Portal>
                    <Popover.Content className="breadcrumb-popover" sideOffset={4} align="start">
                      <ul>
                        {c.hidden.map((h, j) => (
                          <li key={j}>
                            <button type="button" onClick={() => h.routeId && go(h.routeId)}>
                              <BidiText>{h.label}</BidiText>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </Popover.Content>
                  </Popover.Portal>
                </Popover.Root>
                {!isTail && <Sep />}
              </li>
            );
          }
          return (
            <li key={i}>
              {c.isLast || !c.routeId
                ? <BidiText aria-current="page" data-testid="breadcrumb-current">{c.label}</BidiText>
                : (
                  <a href={"/" + c.routeId} onClick={(e) => { e.preventDefault(); go(c.routeId!); }}>
                    <BidiText>{c.label}</BidiText>
                  </a>
                )}
              {!isTail && <Sep />}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default Breadcrumbs;
